import React, { FC, useEffect, useState, useMemo } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Stack,
  CircularProgress,
  SelectChangeEvent,
  useTheme,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { utils, Button as CustomButton } from "@vilocnv/allsetra-core";
import { useSearchParams } from "react-router-dom";
import { DateRangePicker, TableToggleField } from "@vilocnv/allsetra-core";
import moment from "moment";
import "moment/locale/nl";
import { useTranslation } from "react-i18next";
import { preserveSearchParams, BUTTON_STYLES } from "./ridesListHelpers";
import { RidesListProps } from "./RidesList.types";
import RideRow from "./RideRow";
import RidesPagination from "./RidesPagination";
import { isOngoingRide } from "app/data/helpers";

const RidesList: FC<RidesListProps> = ({
  data = [],
  loading = false,
  resolvedAddresses = [],
  resolvedAddressLoading = false,
  loadingRideIds = new Set(),
  cellActions = [],
  primaryButton,
  secondaryButton,
  selectableRows = false,
  onSelectedRowsChange,
  clearSelectedRows,
  translator: t,
  paginationTotalRows,
  showDateRangeFilter,
  showToggleMovementRides,
  showMovementRides,
  onDateRangeChange,
  onMovementRidesChange,
  canViewAddressForRide
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const currentLocale = i18n.language === "nl" ? "nl" : "en";
  const [dateRangeValue, setDateRangeValue] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [showMovementLocal, setShowMovementLocal] =
    useState<boolean>(!!showMovementRides);

  useEffect(() => {
    setShowMovementLocal(!!showMovementRides);
  }, [showMovementRides]);

  useEffect(() => {
    if (clearSelectedRows) {
      setSelectedIds(new Set());
      if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows: [] });
    }
  }, [clearSelectedRows]);

  const handleToggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    if (onSelectedRowsChange) {
      onSelectedRowsChange({
        selectedRows: Array.from(next).map((id) => ({ uniqueId: id }))
      });
    }
  };

  const selectablePageIds = useMemo(
    () =>
      data
        .filter((d: any) => !isOngoingRide(d))
        .map((d: any) => d.uniqueId || d.id),
    [data]
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = new Set(selectedIds);

    if (event.target.checked) {
      selectablePageIds.forEach((id: string) => next.add(id));
    } else {
      selectablePageIds.forEach((id: string) => next.delete(id));
    }

    setSelectedIds(next);
    if (onSelectedRowsChange) {
      onSelectedRowsChange({
        selectedRows: Array.from(next).map((id) => ({ uniqueId: id }))
      });
    }
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const findResolvedAddress = useMemo(
    () => (lat: number, lon: number) =>
      resolvedAddresses?.find((addr: any) => {
        const addrLat = Number(addr.latitude);
        const addrLon = Number(addr.longitude);
        const latNum = Number(lat);
        const lonNum = Number(lon);
        if (
          Number.isNaN(addrLat) ||
          Number.isNaN(addrLon) ||
          Number.isNaN(latNum) ||
          Number.isNaN(lonNum)
        )
          return false;
        const tol = 1e-6;
        return (
          Math.abs(addrLat - latNum) <= tol && Math.abs(addrLon - lonNum) <= tol
        );
      }),
    [resolvedAddresses]
  );

  const currentPage = useMemo(
    () => parseInt(searchParams.get("page") || "1", 10),
    [searchParams]
  );

  const currentPerPage = useMemo(
    () =>
      parseInt(
        searchParams.get("itemsPerPage") ||
          String(utils.getCommonParamsForApi().itemsPerPage || 25),
        10
      ),
    [searchParams]
  );

  const total = useMemo(
    () => Number(paginationTotalRows) || 0,
    [paginationTotalRows]
  );

  const paginationState = useMemo(() => {
    const startRow = total === 0 ? 0 : (currentPage - 1) * currentPerPage + 1;
    const endRow =
      total === 0 ? 0 : Math.min(currentPage * currentPerPage, total);
    const totalPages = total === 0 ? 1 : Math.ceil(total / currentPerPage);
    const canPreviousPage = currentPage > 1;
    const canNextPage = endRow < total;

    return {
      startRow,
      endRow,
      totalPages,
      canPreviousPage,
      canNextPage
    };
  }, [currentPage, currentPerPage, total]);

  const handlePageChange = (_: any, value: number) => {
    const params = preserveSearchParams(searchParams, {
      page: String(value),
      itemsPerPage: String(currentPerPage)
    });
    setSearchParams(params);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<string>) => {
    const newPerPage = parseInt(event.target.value, 10);
    const params = preserveSearchParams(searchParams, {
      page: "1",
      itemsPerPage: String(newPerPage)
    });
    setSearchParams(params);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(total / currentPerPage)) {
      handlePageChange(null, currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(null, currentPage - 1);
    }
  };

  const syncDateRangeParams = (value: any) => {
    const startDate = moment(value.startDate)
      .local()
      .format("YYYY-MM-DDT00:00:00Z");
    const endDate = moment(value.endDate)
      .local()
      .format("YYYY-MM-DDT23:59:59Z");

    const params = preserveSearchParams(searchParams, {
      startDate,
      endDate,
      page: "1" // Reset to page 1 when date range changes
    });

    setSearchParams(params);
  };

  const handleDateRangeSubmit = () => {
    syncDateRangeParams(dateRangeValue);
    if (onDateRangeChange) {
      onDateRangeChange(dateRangeValue);
    }
  };

  const handleDateRangeChange = (value: any) => {
    setDateRangeValue(value);
    if (onDateRangeChange) {
      onDateRangeChange(value);
    }
  };

  const handleToggleShowMovementRides = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    setShowMovementLocal(checked);
    if (onMovementRidesChange) {
      onMovementRidesChange(checked);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        containerType: "inline-size"
      }}
    >
      {/* Topbar - DateRange, Toggle on left | Buttons on right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          "@container (max-width: 1240px)": {
            flexWrap: "wrap",
            gap: 2
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flexWrap: "wrap",
            "@container (max-width: 1240px)": {
              width: "100%",
              justifyContent: "flex-start"
            }
          }}
        >
          {showDateRangeFilter && (
            <DateRangePicker
              name="dateRangeFilter"
              value={dateRangeValue}
              onChange={(value: any) => handleDateRangeChange(value)}
              onSubmit={handleDateRangeSubmit}
              translator={t}
              theme={undefined}
            />
          )}
          {showToggleMovementRides && (
            <TableToggleField
              label={t ? t("common.showMovementRides") : "Show movement rides"}
              name="showMovementRides"
              onChange={handleToggleShowMovementRides}
              checked={showMovementLocal}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            "@container (max-width: 1240px)": {
              width: "100%",
              justifyContent: "flex-start"
            }
          }}
        >
          {secondaryButton && (
            <Box
              sx={{
                "& button": {
                  ...BUTTON_STYLES.UPDATE_ITEMS.normal,
                  "&:hover": BUTTON_STYLES.UPDATE_ITEMS.hover,
                  "&:disabled": BUTTON_STYLES.UPDATE_ITEMS.disabled
                }
              }}
            >
              <CustomButton
                variant={secondaryButton.variant as any}
                size={secondaryButton.size as any}
                onClick={secondaryButton.onClick}
                disabled={secondaryButton.disabled}
                endIcon={secondaryButton.endIcon}
              >
                {secondaryButton.text}
              </CustomButton>
            </Box>
          )}
          {primaryButton && (
            <Box
              sx={{
                "& button": {
                  ...BUTTON_STYLES.CANCEL.normal,
                  "&:hover": BUTTON_STYLES.CANCEL.hover,
                  "&:disabled": BUTTON_STYLES.CANCEL.disabled
                }
              }}
            >
              <CustomButton
                variant={primaryButton.variant as any}
                size={primaryButton.size as any}
                onClick={primaryButton.onClick}
                disabled={primaryButton.disabled}
              >
                {primaryButton.text}
              </CustomButton>
            </Box>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          "@container (max-width: 1240px)": {
            overflowX: "auto",
            overflowY: "visible"
          }
        }}
      >
        {selectableRows &&
          data.length > 0 &&
          (() => {
            const isAllPageSelected =
              selectablePageIds.length > 0 &&
              selectablePageIds.every((id) => selectedIds.has(id));
            const isIndeterminate =
              !isAllPageSelected &&
              selectablePageIds.some((id) => selectedIds.has(id));

            return (
              <Box sx={{ mb: 1, ml: 1, display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllPageSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      disabled={selectablePageIds.length === 0}
                    />
                  }
                  label={t ? t("common.selectAll") : "Select All"}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            );
          })()}
        <Box
          sx={{
            "@container (max-width: 1240px)": {
              minWidth: "1240px"
            }
          }}
        >
          {data.map((row, index) => {
            const startAddress = findResolvedAddress(
              row.startLatitude,
              row.startLongitude
            )?.address;
            const endAddress = isOngoingRide(row)
              ? undefined
              : findResolvedAddress(row.endLatitude, row.endLongitude)?.address;

            return (
              <RideRow
                key={row.uniqueId || row.id}
                row={row}
                index={index}
                selectedIds={selectedIds}
                selectableRows={selectableRows}
                onToggle={handleToggle}
                startAddress={startAddress}
                endAddress={endAddress}
                resolvedAddressLoading={resolvedAddressLoading}
                loadingRideIds={loadingRideIds}
                showMovementLocal={showMovementLocal}
                cellActions={cellActions}
                canViewAddressForRide={canViewAddressForRide}
                onMenuOpen={openMenu}
                translator={t}
                currentLocale={currentLocale}
              />
            );
          })}
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {menuRow &&
          cellActions
            .filter((action) => (action.when ? action.when(menuRow) : true))
            .map((action, idx) => (
              <MenuItem
                key={idx}
                onClick={() => {
                  action.onClick(menuRow);
                  closeMenu();
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {action.icon}
                  <span>{action.name}</span>
                </Stack>
              </MenuItem>
            ))}
      </Menu>

      {/* Pagination footer */}
      <RidesPagination
        paginationState={paginationState}
        currentPerPage={currentPerPage}
        total={total}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        translator={t}
      />
    </Box>
  );
};

export default RidesList;
